import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Paper, CircularProgress, Alert } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import styles from './GmailIntegration.module.scss';
import gmailIntegrationApi, { IGmailIntegration } from '~/apis/gmailIntegration';
import { notify, getUserIdFromToken } from '~/utils/helper';
import { baseURL } from '~/utils/constants/env';

const GmailIntegration = () => {
  const { t } = useTranslation(['admin/settings']);
  const [searchParams, setSearchParams] = useSearchParams();
  const [integrations, setIntegrations] = useState<IGmailIntegration[]>([]);
  const [loading, setLoading] = useState(false);
  const [connecting, setConnecting] = useState(false);

  // Handle OAuth callback from URL params
  useEffect(() => {
    const gmailStatus = searchParams.get('gmail');
    const reason = searchParams.get('reason');

    if (gmailStatus === 'connected') {
      notify('success', t('gmailIntegration.connectSuccess'));
      // Remove query params
      searchParams.delete('gmail');
      setSearchParams(searchParams, { replace: true });
      // Refresh integrations
      fetchIntegrations();
    } else if (gmailStatus === 'error') {
      const errorMessage = reason 
        ? t(`gmailIntegration.errors.${reason}`, { defaultValue: t('gmailIntegration.connectError') })
        : t('gmailIntegration.connectError');
      notify('error', errorMessage);
      // Remove query params
      searchParams.delete('gmail');
      searchParams.delete('reason');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, t, setSearchParams]);

  // Fetch integrations on mount
  useEffect(() => {
    fetchIntegrations();
  }, []);

  const fetchIntegrations = async () => {
    setLoading(true);
    try {
      const response = await gmailIntegrationApi.getIntegrations();
      if (response.data?.data) {
        setIntegrations(response.data.data);
      }
    } catch (error: any) {
      console.error('Failed to fetch Gmail integrations:', error);
      // Don't show error if it's just no integrations found
      if (error?.response?.status !== 404) {
        notify('error', error?.response?.data?.message || t('gmailIntegration.fetchError'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleConnectGmail = () => {
    setConnecting(true);
    // Get userId from token
    const userId = getUserIdFromToken();
    if (!userId) {
      notify('error', t('gmailIntegration.userIdError'));
      setConnecting(false);
      return;
    }
    // Redirect to backend OAuth endpoint with userId
    const domain = baseURL ? baseURL : `https://christian-brothers.alphabravodev.com`;
    const connectUrl = `${domain}/api/integrations/google/gmail/connect?userId=${userId}`;
    window.location.href = connectUrl;
  };

  const handleDisconnectGmail = async () => {
    if (!window.confirm(t('gmailIntegration.disconnectConfirm'))) {
      return;
    }

    setLoading(true);
    try {
      await gmailIntegrationApi.disconnectGmail();
      notify('success', t('gmailIntegration.disconnectSuccess'));
      setIntegrations([]);
    } catch (error: any) {
      notify('error', error?.response?.data?.message || t('gmailIntegration.disconnectError'));
    } finally {
      setLoading(false);
    }
  };

  const connectedIntegration = integrations.find(
    (integration) => integration.status === 'connected' && integration.provider === 'google'
  );

  return (
    <Box className={styles.gmailIntegration}>
      <Typography variant="h5" className={styles.title}>
        {t('gmailIntegration.title')}
      </Typography>
      <Typography variant="body2" className={styles.description}>
        {t('gmailIntegration.description')}
      </Typography>

      <Paper className={styles.contentContainer}>
        {loading && !connecting ? (
          <Box className={styles.loadingContainer}>
            <CircularProgress size={40} />
            <Typography className={styles.loadingText}>
              {t('gmailIntegration.loading')}
            </Typography>
          </Box>
        ) : connectedIntegration ? (
          <Box className={styles.connectedContainer}>
            <Alert severity="success" className={styles.statusAlert}>
              {t('gmailIntegration.connected')}
            </Alert>
            <Box className={styles.connectionInfo}>
              <Typography className={styles.infoLabel}>
                {t('gmailIntegration.emailAddress')}:
              </Typography>
              <Typography className={styles.infoValue}>
                {connectedIntegration.emailAddress}
              </Typography>
            </Box>
            <Box className={styles.connectionInfo}>
              <Typography className={styles.infoLabel}>
                {t('gmailIntegration.connectedAt')}:
              </Typography>
              <Typography className={styles.infoValue}>
                {new Date(connectedIntegration.connectedAt).toLocaleString()}
              </Typography>
            </Box>
            <Button
              variant="outlined"
              color="error"
              onClick={handleDisconnectGmail}
              className={styles.disconnectButton}
              disabled={loading}
            >
              {t('gmailIntegration.disconnect')}
            </Button>
          </Box>
        ) : (
          <Box className={styles.notConnectedContainer}>
            <Alert severity="info" className={styles.statusAlert}>
              {t('gmailIntegration.notConnected')}
            </Alert>
            <Typography className={styles.connectDescription}>
              {t('gmailIntegration.connectDescription')}
            </Typography>
            <Button
              variant="contained"
              onClick={handleConnectGmail}
              className={styles.connectButton}
              disabled={connecting}
              startIcon={connecting ? <CircularProgress size={20} /> : null}
            >
              {connecting ? t('gmailIntegration.connecting') : t('gmailIntegration.connect')}
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default GmailIntegration;

